import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Copy, Plus, Trash2, ExternalLink, Settings, Webhook, TestTube } from 'lucide-react';
import Button from '../ui/Button';
import FormField from '../ui/FormField';
import Modal from '../ui/Modal';
import Card from '../ui/Card';

const ApiSettingsSection = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showWebhookModal, setShowWebhookModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  
  // Form states
  const [createForm, setCreateForm] = useState({ keyName: '' });
  const [webhookForm, setWebhookForm] = useState({
    webhookUrl: '',
    webhookEnabled: false,
    webhookRetries: 3,
    webhookRetryDelayMinutes: 5
  });
  
  // UI states
  const [creating, setCreating] = useState(false);
  const [updatingWebhook, setUpdatingWebhook] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [copiedKeys, setCopiedKeys] = useState({});
  
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('loginAsToken') || localStorage.getItem('token');
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/user/api-keys/`, {
        headers: getAuthHeaders()
      });
      setApiKeys(response.data);
      setError('');
    } catch (error) {
      setError('Failed to fetch API keys');
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const createApiKey = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const response = await axios.post(`${API_BASE_URL}/api/user/api-keys/`, createForm, {
        headers: getAuthHeaders()
      });
      
      setSuccess(`API key created successfully: ${response.data.api_key}`);
      setCreateForm({ keyName: '' });
      setShowCreateModal(false);
      
      // Store the full API key for the newly created key
      const newApiKeyId = response.data.api_key_id;
      const fullApiKey = response.data.api_key;
      
      // Refresh API keys and add full key to state
      await fetchApiKeys();
      
      // Store full API key in a separate state for newly created keys
      setApiKeys(prev => prev.map(key => 
        key.id === newApiKeyId 
          ? { ...key, api_key_full: fullApiKey }
          : key
      ));
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to create API key');
    } finally {
      setCreating(false);
    }
  };

  const updateWebhookConfig = async (e) => {
    e.preventDefault();
    setUpdatingWebhook(true);
    setError('');

    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/user/api-keys/${selectedApiKey.id}/webhook`,
        webhookForm,
        { headers: getAuthHeaders() }
      );
      
      setSuccess('Webhook configuration updated successfully');
      setShowWebhookModal(false);
      fetchApiKeys();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update webhook configuration');
    } finally {
      setUpdatingWebhook(false);
    }
  };

  const deleteApiKey = async () => {
    setDeleting(true);
    setError('');

    try {
      await axios.delete(`${API_BASE_URL}/api/user/api-keys/${selectedApiKey.id}`, {
        headers: getAuthHeaders()
      });
      
      setSuccess('API key deleted successfully');
      setShowDeleteModal(false);
      setSelectedApiKey(null);
      fetchApiKeys();
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to delete API key');
    } finally {
      setDeleting(false);
    }
  };

  const testWebhook = async (apiKey) => {
    setTesting(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/user/api-keys/${apiKey.id}/webhook/test`,
        { webhook_url: apiKey.webhook_url },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.status === 'success') {
        setSuccess('Webhook test successful! Check your endpoint for the test payload.');
      } else {
        setError('Webhook test failed. Please check your endpoint URL and try again.');
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to test webhook');
    } finally {
      setTesting(false);
    }
  };

  const toggleKeyVisibility = (keyId) => {
    setVisibleKeys(prev => ({
      ...prev,
      [keyId]: !prev[keyId]
    }));
  };

  const copyToClipboard = async (text, keyId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKeys(prev => ({ ...prev, [keyId]: true }));
      setTimeout(() => {
        setCopiedKeys(prev => ({ ...prev, [keyId]: false }));
      }, 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const openWebhookModal = (apiKey) => {
    setSelectedApiKey(apiKey);
    setWebhookForm({
      webhookUrl: apiKey.webhook_url || '',
      webhookEnabled: apiKey.webhook_enabled,
      webhookRetries: apiKey.webhook_retries,
      webhookRetryDelayMinutes: apiKey.webhook_retry_delay_minutes
    });
    setShowWebhookModal(true);
  };

  const openDeleteModal = (apiKey) => {
    setSelectedApiKey(apiKey);
    setShowDeleteModal(true);
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <div style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Loading API settings...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>API Access</h2>
            <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)', marginTop: 4 }}>
              Manage your API keys and webhook configuration for external access
            </p>
          </div>
          <Button 
            onClick={() => setShowCreateModal(true)}
            icon={<Plus size={16} />}
            disabled={apiKeys.length >= 5}
          >
            Create API Key
          </Button>
        </div>

        {/* API Documentation Link */}
        <Card style={{ marginBottom: 24, backgroundColor: 'var(--color-bg-app)' }}>
          <Card.Content>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--color-info-fg)' }}>API Documentation</h3>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)', marginTop: 4 }}>
                  View complete API reference, authentication guide, and code examples
                </p>
              </div>
              <Button
                variant="secondary"
                onClick={() => window.open(`${API_BASE_URL}/docs`, '_blank')}
                icon={<ExternalLink size={16} />}
              >
                View Docs
              </Button>
            </div>
          </Card.Content>
        </Card>

        {error && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--color-danger-bg)',
            border: '1px solid var(--color-danger-border)',
            borderRadius: 8,
            color: 'var(--color-danger-hover)',
            fontSize: 14,
            marginBottom: 16
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--color-success-bg)',
            border: '1px solid var(--color-success-border)',
            borderRadius: 8,
            color: '#166534',
            fontSize: 14,
            marginBottom: 16
          }}>
            {success}
          </div>
        )}

        {/* API Keys List */}
        <div style={{ display: 'grid', gap: 16 }}>
          {apiKeys.length === 0 ? (
            <Card>
              <Card.Content style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 16 }}>
                  No API keys created yet
                </div>
                <Button onClick={() => setShowCreateModal(true)} icon={<Plus size={16} />}>
                  Create Your First API Key
                </Button>
              </Card.Content>
            </Card>
          ) : (
            apiKeys.map((apiKey) => (
              <Card key={apiKey.id}>
                <Card.Header>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                        {apiKey.key_name || 'Unnamed Key'}
                      </h3>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                        Created {formatDate(apiKey.created_at)}
                      </div>
                    </div>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '2px 8px',
                      borderRadius: 12,
                      fontSize: 12,
                      fontWeight: 500,
                      backgroundColor: apiKey.is_active ? '#dcfce7' : 'var(--color-danger-bg-strong)',
                      color: apiKey.is_active ? '#166534' : 'var(--color-danger-hover)'
                    }}>
                      {apiKey.is_active ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                </Card.Header>
                <Card.Content>
                  {/* API Key Display */}
                  <div style={{ marginBottom: 16 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: 4 }}>
                      API Key
                    </label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input
                        type={visibleKeys[apiKey.id] ? 'text' : 'password'}
                        value={visibleKeys[apiKey.id] ? apiKey.api_key_full || apiKey.api_key_preview : apiKey.api_key_preview}
                        readOnly
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: '1px solid var(--color-border-strong)',
                          borderRadius: 6,
                          fontSize: 14,
                          fontFamily: 'monospace',
                          backgroundColor: 'var(--color-bg-subtle)'
                        }}
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => toggleKeyVisibility(apiKey.id)}
                        icon={visibleKeys[apiKey.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                      >
                        {visibleKeys[apiKey.id] ? 'Hide' : 'Show'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => copyToClipboard(visibleKeys[apiKey.id] ? apiKey.api_key_full || apiKey.api_key_preview : apiKey.api_key_preview, apiKey.id)}
                        icon={<Copy size={14} />}
                      >
                        {copiedKeys[apiKey.id] ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  </div>

                  {/* Usage Stats */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Usage Count</div>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{apiKey.usage_count}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Rate Limit</div>
                      <div style={{ fontSize: 18, fontWeight: 600 }}>{apiKey.rate_limit_per_hour}/hr</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Last Used</div>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>
                        {apiKey.last_used_at ? formatDate(apiKey.last_used_at) : 'Never'}
                      </div>
                    </div>
                  </div>

                  {/* Webhook Status */}
                  <div style={{ 
                    padding: '12px 16px', 
                    backgroundColor: apiKey.webhook_enabled ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)',
                    border: '1px solid ' + (apiKey.webhook_enabled ? 'var(--color-success-border)' : 'var(--color-border)'),
                    borderRadius: 8,
                    marginBottom: 16
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Webhook size={16} color={apiKey.webhook_enabled ? '#16a34a' : 'var(--color-text-muted)'} />
                        <span style={{ fontSize: 14, fontWeight: 500 }}>
                          Webhook {apiKey.webhook_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      {apiKey.webhook_enabled && apiKey.webhook_url && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => testWebhook(apiKey)}
                          loading={testing}
                          icon={<TestTube size={14} />}
                        >
                          Test
                        </Button>
                      )}
                    </div>
                    {apiKey.webhook_enabled && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        URL: {apiKey.webhook_url || 'Not configured'}<br />
                        Retries: {apiKey.webhook_retries}, Delay: {apiKey.webhook_retry_delay_minutes}m
                        {apiKey.webhook_failure_count > 0 && (
                          <span style={{ color: 'var(--color-danger-hover)' }}> • {apiKey.webhook_failure_count} failures</span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => openWebhookModal(apiKey)}
                      icon={<Settings size={14} />}
                    >
                      Configure Webhook
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => openDeleteModal(apiKey)}
                      icon={<Trash2 size={14} />}
                    >
                      Delete
                    </Button>
                  </div>
                </Card.Content>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New API Key"
        size="md"
      >
        <form onSubmit={createApiKey}>
          <div style={{ marginBottom: 20 }}>
            <FormField
              label="Key Name (Optional)"
              type="text"
              value={createForm.keyName}
              onChange={(e) => setCreateForm({ ...createForm, keyName: e.target.value })}
              placeholder="e.g., Production API, Development Key"
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>
              Give your API key a descriptive name to help you identify it later.
            </p>
          </div>

          <div style={{ 
            padding: '12px 16px', 
            backgroundColor: 'var(--color-info-bg)', 
            border: '1px solid var(--color-info-border)', 
            borderRadius: 8,
            marginBottom: 20
          }}>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-info-hover)', marginBottom: 4 }}>
              Important Security Notes:
            </div>
            <ul style={{ fontSize: 12, color: 'var(--color-info-fg)', margin: 0, paddingLeft: 16 }}>
              <li>You can show/hide your API key at any time using the Show/Hide button</li>
              <li>Store it securely and never share it publicly</li>
              <li>Rate limit: 100 requests per hour per key</li>
              <li>Maximum 5 API keys per account</li>
            </ul>
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowCreateModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={creating}>
              Create API Key
            </Button>
          </div>
        </form>
      </Modal>

      {/* Configure Webhook Modal */}
      <Modal
        isOpen={showWebhookModal}
        onClose={() => setShowWebhookModal(false)}
        title="Configure Webhook"
        size="lg"
      >
        <form onSubmit={updateWebhookConfig}>
          <div style={{ marginBottom: 20 }}>
            <FormField
              label="Webhook URL"
              type="url"
              value={webhookForm.webhookUrl}
              onChange={(e) => setWebhookForm({ ...webhookForm, webhookUrl: e.target.value })}
              placeholder="https://your-app.com/webhook/apollo-scraper"
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={webhookForm.webhookEnabled}
                onChange={(e) => setWebhookForm({ ...webhookForm, webhookEnabled: e.target.checked })}
                style={{ width: 16, height: 16 }}
              />
              <span style={{ fontSize: 14, fontWeight: 500 }}>Enable webhook notifications</span>
            </label>
          </div>

          {webhookForm.webhookEnabled && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <FormField
                  label="Retry Attempts"
                  type="number"
                  value={webhookForm.webhookRetries}
                  onChange={(e) => setWebhookForm({ ...webhookForm, webhookRetries: parseInt(e.target.value) })}
                  min="0"
                  max="10"
                />
                <FormField
                  label="Retry Delay (minutes)"
                  type="number"
                  value={webhookForm.webhookRetryDelayMinutes}
                  onChange={(e) => setWebhookForm({ ...webhookForm, webhookRetryDelayMinutes: parseInt(e.target.value) })}
                  min="3"
                  max="30"
                />
              </div>

              <div style={{ 
                padding: '12px 16px', 
                backgroundColor: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: 8,
                marginBottom: 20
              }}>
                <div style={{ fontSize: 14, fontWeight: 500, color: '#0c4a6e', marginBottom: 8 }}>
                  Webhook Payload Example:
                </div>
                <pre style={{ 
                  fontSize: 12, 
                  color: '#0f172a', 
                  margin: 0, 
                  backgroundColor: 'var(--color-bg-app)',
                  padding: 8,
                  borderRadius: 4,
                  overflow: 'auto'
                }}>
{`{
  "job_id": "123e4567-e89b-12d3-a456-426614174000",
  "status": "completed",
  "download_url": "https://...",
  "contacts_scraped": 150,
  "apollo_url": "https://apollo.io/...",
  "created_at": "2025-01-15T10:30:00Z",
  "completed_at": "2025-01-15T11:15:00Z",
  "error_message": null
}`}
                </pre>
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowWebhookModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" loading={updatingWebhook}>
              Save Configuration
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete API Key"
        size="md"
      >
        <div style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 16 }}>
            Are you sure you want to delete the API key "{selectedApiKey?.key_name || 'Unnamed Key'}"?
          </p>
          <div style={{
            padding: '12px 16px',
            backgroundColor: 'var(--color-danger-bg)',
            border: '1px solid var(--color-danger-border)',
            borderRadius: 8
          }}>
            <p style={{ fontSize: 13, color: 'var(--color-danger-hover)', margin: 0 }}>
              <strong>Warning:</strong> This action cannot be undone. Any applications using this API key will lose access immediately.
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={deleteApiKey}
            loading={deleting}
          >
            Delete API Key
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ApiSettingsSection;